import React, { useRef, MutableRefObject } from 'react';
import uuid from 'uuid-random';
import { StyledDeleteButton } from '../Styled/StyledButtons';
import {
  StyledContainer,
  StyledBlock,
  StyledLink,
  StyledLinkWrapper,
  StyledSubTitle,
  StyledContentWrapper,
  StyledCurrentlyView,
  StyledLeftSideLinks,
  StyledTextType,
  SavedAsIWPLabel,
  EllipsisWrapper,
} from '../Styled/StyledRelatedNav';
import { MarginWrapper } from '../Styled';
import { Image } from '../Image/Image';
import { View, FullScreen, DeleteIcon } from '../../assets/icons/Related';
import { MAIN_CONTENT_SIZE, SIZES } from '../../constants';
import { RelatedType } from '../../types';
import { Tooltip } from 'react-magma-dom';
import { useTooltipForLongName } from '../../hooks/useLongNameTooltip';

interface IRelated {
  textType: string;
  title: string;
  imgSrc: string;
  viewLink: string;
  isRelated?: boolean;
  fullScreenLink?: string;
  relatedType?: string;
  isRelatedDefault?: boolean;
  isRelatedCurrentlyInView?: boolean;
  handleView?: () => void;
  handleFullScreen?: () => void;
  handleDelete?: () => void;
}

const VIEW = 'VIEW';
const VIEW_LESSON = 'VIEW LESSON';
const CURRENTLY_VIEW = 'Currently in View';
const FULL_SCREEN = 'FULL SCREEN';
const DELETE = 'DELETE';

// eslint-disable-next-line complexity
export const Related = (props: IRelated): JSX.Element => {
  const {
    textType,
    title,
    imgSrc,
    viewLink,
    isRelated,
    fullScreenLink,
    relatedType,
    isRelatedDefault,
    isRelatedCurrentlyInView,
  } = props;
  const { handleView, handleFullScreen, handleDelete } = props;

  const subtitleRef = useRef() as MutableRefObject<HTMLDivElement>;
  const ellipsisWrapperRef = useRef() as MutableRefObject<HTMLDivElement>;

  const isTooltip = useTooltipForLongName(subtitleRef, ellipsisWrapperRef);

  const viewLinkAriaLabelText = isRelated ? `${textType}, ${title}, View` : 'View lesson';
  const fullScreenButtonAriaLabelText = `${textType}, ${title}, Full Screen`;
  const deleteButtonAriaLabelText = `${textType}, ${title}, Delete`;

  const imgAltText = `related-${isRelated ? 'exemplar' : 'lesson'}-image`;

  const showName = (withTooltip: boolean): JSX.Element => {
    const defaultNameElement = <EllipsisWrapper ref={ellipsisWrapperRef}>{title}</EllipsisWrapper>;

    const tooltipStyle = {
      minWidth: 'fit-content',
    };

    const tooltipContainerStyle = {
      width: '100%',
    };

    return withTooltip ? (
      <Tooltip tooltipStyle={tooltipStyle} containerStyle={tooltipContainerStyle} content={title}>
        {defaultNameElement}
      </Tooltip>
    ) : (
      defaultNameElement
    );
  };

  return (
    <StyledContainer testId="related-item" key={uuid()}>
      <StyledTextType>{textType}</StyledTextType>
      <StyledBlock width={MAIN_CONTENT_SIZE.S[0]}>
        <StyledContentWrapper>
          <StyledSubTitle ref={subtitleRef}>
            {showName(isTooltip)}
            {isRelated && relatedType && relatedType === RelatedType.saved_proformas && (
              <SavedAsIWPLabel>Interactive Writing Proforma</SavedAsIWPLabel>
            )}
          </StyledSubTitle>
          <StyledLinkWrapper justifyContent="space-between">
            <StyledLeftSideLinks>
              {isRelated && isRelatedCurrentlyInView ? (
                <StyledCurrentlyView>{CURRENTLY_VIEW}</StyledCurrentlyView>
              ) : (
                <StyledLeftSideLinks>
                  <StyledLink
                    onClick={isRelated ? handleView : undefined}
                    to={viewLink}
                    tabIndex={0}
                    aria-label={viewLinkAriaLabelText}
                  >
                    {isRelated ? VIEW : VIEW_LESSON} <View />
                  </StyledLink>
                  {isRelated && fullScreenLink && (
                    <StyledLink
                      onClick={handleFullScreen}
                      to={fullScreenLink}
                      aria-label={fullScreenButtonAriaLabelText}
                    >
                      {FULL_SCREEN}
                      <FullScreen />
                    </StyledLink>
                  )}
                </StyledLeftSideLinks>
              )}
            </StyledLeftSideLinks>
            {isRelated && !isRelatedDefault && !isRelatedCurrentlyInView && (
              <StyledDeleteButton aria-label={deleteButtonAriaLabelText} onClick={handleDelete}>
                {DELETE} <DeleteIcon />
              </StyledDeleteButton>
            )}
          </StyledLinkWrapper>
        </StyledContentWrapper>
        <MarginWrapper margin="16px 20px 16px 22.2px">
          <Image src={imgSrc} alt={imgAltText} width={SIZES.S} height={SIZES.XS} />
        </MarginWrapper>
      </StyledBlock>
    </StyledContainer>
  );
};
